// Import model
import { CalculatedField } from '../models/CalculatedField';

// Import base class serializer
import { FieldSerializer } from './FieldSerializer';

// Import dependency types (models and enums used in properties)
import { Calculation } from '../models/Calculation';
import { FieldOptions } from '../models/FieldOptions';

// Import dependency serializers (for complex types)
import { CalculationSerializer } from './CalculationSerializer';
import { FieldOptionsSerializer } from './FieldOptionsSerializer';

// Import derived type serializers (for polymorphic deserialization)

/**
 * Serializer for CalculatedField - handles JSON serialization and deserialization with type validation
 */
export class CalculatedFieldSerializer {
  /**
   * Deserializes a JSON string into a CalculatedField instance
   */
  static deserialize(json: string): CalculatedField {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a CalculatedField instance
   */
  static fromObject(obj: any): CalculatedField {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to CalculatedField');
    }


    const instance: CalculatedField = {
      t: obj['_t'] as string,
      description: obj['Description'] as string | undefined,
      apiName: obj['ApiName'] as string | undefined,
      defaultValue: obj['DefaultValue'] as any | undefined,
      isRequired: obj['IsRequired'] as boolean | undefined,
      options: obj['Options'] ? FieldOptionsSerializer.fromObject(obj['Options']) : undefined as any,
      name: obj['Name'] as string,
      label: obj['Label'] as string | undefined,
      enable: obj['Enable'] ? (obj['Enable'] as any[]).map((item: any) =>
        item as string
      ) : [] as any,
      visible: obj['Visible'] ? (obj['Visible'] as any[]).map((item: any) =>
        item as string
      ) : [] as any,
      field: obj['Field'] ? FieldSerializer.fromObject(obj['Field']) : undefined as any,
      calculation: CalculationSerializer.fromObject(obj['Calculation']),
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of CalculatedField instances
   */
  static deserializeArray(json: string): CalculatedField[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a CalculatedField instance to a JSON string
   */
  static serialize(instance: CalculatedField): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a CalculatedField instance to a plain object
   */
  static toObject(instance: CalculatedField): any {
    return {
      '_t': instance.t,
      'Description': instance.description,
      'ApiName': instance.apiName,
      'DefaultValue': instance.defaultValue,
      'IsRequired': instance.isRequired,
      'Options': instance.options ? FieldOptionsSerializer.toObject(instance.options) : undefined,
      'Name': instance.name,
      'Label': instance.label,
      'Enable': instance.enable?.map((item: any) =>
        item
      ),
      'Visible': instance.visible?.map((item: any) =>
        item
      ),
      'Field': instance.field ? FieldSerializer.toObject(instance.field) : undefined,
      'Calculation': instance.calculation ? CalculationSerializer.toObject(instance.calculation) : undefined,
    };
  }

  /**
   * Serializes an array of CalculatedField instances to a JSON string
   */
  static serializeArray(instances: CalculatedField[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
